/** @file Poses a skeleton for the editor: the setup pose, one animation at a time, or a mix preview. */
import { applyAnimation } from '@pose2d/core/anim/applyAnimation';
import { resetPose } from '@pose2d/core/pose/resetPose';
import { updatePose } from '@pose2d/core/pose/updatePose';
import { applyState } from '@pose2d/core/state/applyState';
import type { AnimState, Pose, Sample, SkeletonData, SkinData } from '@pose2d/core/types';
import { invariant } from '@pose2d/std/invariant';

/** What the viewport shows: setup (Setup mode), the current animation at a time (seconds), or a running mix. */
export type PoseAt = { kind: 'setup' } | { kind: 'animation'; anim: number; time: number } | { kind: 'mix'; state: AnimState };

const NO_SKIN: SkinData = { name: '', map: new Map() };

/**
 * Writes the pose for `at` and runs updatePose. `scratch` holds three distinct createSample(data) (applyState's
 * rule, R10-06; the single-animation path uses the first). Attachments resolve through the default skin.
 * `preview` (W3-30: the viewport's drag preview) writes local values before the final updatePose, so IK solves live:
 * after resetPose (+ applyAnimation), or after applyState for a mix, which is then followed by one more updatePose.
 */
export function poseAt(data: SkeletonData, pose: Pose, scratch: Sample[], at: PoseAt, preview?: (pose: Pose) => void): void {
  if (at.kind === 'mix') {
    applyState(at.state, data, pose, scratch);
    if (preview !== undefined) {
      preview(pose);
      updatePose(pose, data);
    }
    return;
  }
  const skin = data.skins[data.defaultSkin] ?? NO_SKIN;
  resetPose(pose, data, skin);
  if (at.kind === 'animation') {
    const sample = scratch[0];
    invariant(sample !== undefined, 'poseAt: scratch needs createSample(data)');
    applyAnimation(pose, data, at.anim, at.time, { alpha: 1, blend: 'replace', sample, skin });
  }
  preview?.(pose);
  updatePose(pose, data);
}
